// src/app/core/store/claim-analytics/claim-analytics.reducer.ts

import { createReducer, on } from '@ngrx/store';
import {
  ClaimAnalyticsState,
  initialClaimAnalyticsState,
  AsyncSlice,
  emptySlice,
} from './claim-analytics.state';
import {
  CaProductActions,
  CaConfigActions,
  CaLookupActions,
  CaOverviewActions,
  CaTimeSeriesActions,
  CaBreakdownActions,
  CaRankingsActions,
  CaComparisonActions,
} from './claim-analytics.actions';

function setLoading<T>(slice: AsyncSlice<T>): AsyncSlice<T> {
  return { ...slice, loading: true, error: null };
}

function setSuccess<T>(data: T): AsyncSlice<T> {
  return { data, loading: false, loaded: true, error: null };
}

function setFailure<T>(slice: AsyncSlice<T>, error: string): AsyncSlice<T> {
  return { ...slice, loading: false, error };
}

export const claimAnalyticsReducer = createReducer(
  initialClaimAnalyticsState,

  // ── Products ──
  on(CaProductActions.loadProducts, (state): ClaimAnalyticsState => ({
    ...state, productsLoading: true,
  })),
  on(CaProductActions.loadProductsSuccess, (state, { products }): ClaimAnalyticsState => ({
    ...state, products, productsLoading: false, productsLoaded: true,
  })),
  on(CaProductActions.loadProductsFailure, (state): ClaimAnalyticsState => ({
    ...state, productsLoading: false,
  })),
  on(CaProductActions.selectProduct, (state, { productTypeId }): ClaimAnalyticsState => ({
    ...state, selectedProductTypeId: productTypeId,
  })),

  // ── Config ──
  on(CaConfigActions.loadConfig, (state): ClaimAnalyticsState => ({
    ...state, configLoading: true,
  })),
  on(CaConfigActions.loadConfigSuccess, (state, { productTypeId, config }): ClaimAnalyticsState => ({
    ...state,
    configLoading: false,
    configs: { ...state.configs, [productTypeId]: config },
  })),
  on(CaConfigActions.loadConfigFailure, (state): ClaimAnalyticsState => ({
    ...state, configLoading: false,
  })),

  // ── Lookups ──
  on(CaLookupActions.loadCascoCausesSuccess, (state, { causes }): ClaimAnalyticsState => ({
    ...state, cascoCauses: causes, cascoCausesLoaded: true,
  })),
  on(CaLookupActions.loadHealthBenefitTypesSuccess, (state, { types }): ClaimAnalyticsState => ({
    ...state, healthBenefitTypes: types, healthBenefitTypesLoaded: true,
  })),

  // ── Overview ──
  on(CaOverviewActions.load, (state): ClaimAnalyticsState => ({
    ...state, overview: setLoading(state.overview),
  })),
  on(CaOverviewActions.success, (state, { data }): ClaimAnalyticsState => ({
    ...state, overview: setSuccess(data),
  })),
  on(CaOverviewActions.failure, (state, { error }): ClaimAnalyticsState => ({
    ...state, overview: setFailure(state.overview, error),
  })),
  on(CaOverviewActions.clear, (state): ClaimAnalyticsState => ({
    ...state, overview: emptySlice(),
  })),

  // ── TimeSeries ──
  on(CaTimeSeriesActions.load, (state): ClaimAnalyticsState => ({
    ...state, timeSeries: setLoading(state.timeSeries),
  })),
  on(CaTimeSeriesActions.success, (state, { data }): ClaimAnalyticsState => ({
    ...state, timeSeries: setSuccess(data),
  })),
  on(CaTimeSeriesActions.failure, (state, { error }): ClaimAnalyticsState => ({
    ...state, timeSeries: setFailure(state.timeSeries, error),
  })),
  on(CaTimeSeriesActions.clear, (state): ClaimAnalyticsState => ({
    ...state, timeSeries: emptySlice(),
  })),

  // ── Breakdown ──
  on(CaBreakdownActions.load, (state): ClaimAnalyticsState => ({
    ...state, breakdown: setLoading(state.breakdown),
  })),
  on(CaBreakdownActions.success, (state, { data }): ClaimAnalyticsState => ({
    ...state, breakdown: setSuccess(data),
  })),
  on(CaBreakdownActions.failure, (state, { error }): ClaimAnalyticsState => ({
    ...state, breakdown: setFailure(state.breakdown, error),
  })),
  on(CaBreakdownActions.clear, (state): ClaimAnalyticsState => ({
    ...state, breakdown: emptySlice(),
  })),

  // ── Rankings ──
  on(CaRankingsActions.load, (state): ClaimAnalyticsState => ({
    ...state, rankings: setLoading(state.rankings),
  })),
  on(CaRankingsActions.success, (state, { data }): ClaimAnalyticsState => ({
    ...state, rankings: setSuccess(data),
  })),
  on(CaRankingsActions.failure, (state, { error }): ClaimAnalyticsState => ({
    ...state, rankings: setFailure(state.rankings, error),
  })),
  on(CaRankingsActions.clear, (state): ClaimAnalyticsState => ({
    ...state, rankings: emptySlice(),
  })),

  // ── Comparison ──
  on(CaComparisonActions.load, (state): ClaimAnalyticsState => ({
    ...state, comparison: setLoading(state.comparison),
  })),
  on(CaComparisonActions.success, (state, { data }): ClaimAnalyticsState => ({
    ...state, comparison: setSuccess(data),
  })),
  on(CaComparisonActions.failure, (state, { error }): ClaimAnalyticsState => ({
    ...state, comparison: setFailure(state.comparison, error),
  })),
  on(CaComparisonActions.clear, (state): ClaimAnalyticsState => ({
    ...state, comparison: emptySlice(),
  })),
);
